import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore';
import type { KhaataCategory, KhaataCategoryFormData } from '../types/khaataCategory';
import type { CategoryOption } from '../constants/khaataCategories';
import { normalizeKhaataType, slugifyCategory } from '../constants/khaataCategories';

const COLLECTION = 'khaata-categories';

export const getKhaataCategories = async (restId: string): Promise<KhaataCategory[]> => {
  const db = getFirestore();
  try {
    const ref = collection(db, COLLECTION);
    const q = query(ref, where('restId', '==', restId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        restId: data.restId,
        name: data.name,
        value: data.value || slugifyCategory(data.name || ''),
        type: normalizeKhaataType(data.type),
        enabled: data.enabled !== false,
      } as KhaataCategory;
    });
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Failed to fetch categories. Please try again later.');
  }
};

export const addKhaataCategory = async (
  category: KhaataCategoryFormData
): Promise<string> => {
  const db = getFirestore();
  try {
    const ref = collection(db, COLLECTION);
    const name = category.name.trim();
    const docRef = await addDoc(ref, {
      restId: category.restId,
      name,
      value: category.value || slugifyCategory(name),
      type: normalizeKhaataType(category.type),
      enabled: category.enabled !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding category:', error);
    throw new Error('Failed to add category. Please try again later.');
  }
};

export const updateKhaataCategory = async (
  id: string,
  category: Partial<KhaataCategoryFormData>
): Promise<void> => {
  const db = getFirestore();
  try {
    const ref = doc(db, COLLECTION, id);
    const name = category.name?.trim();
    await updateDoc(ref, {
      updatedAt: serverTimestamp(),
      ...(name !== undefined
        ? { name, value: category.value || slugifyCategory(name) }
        : {}),
      ...(category.type !== undefined
        ? { type: normalizeKhaataType(category.type) }
        : {}),
      ...(category.enabled !== undefined ? { enabled: category.enabled } : {}),
    });
  } catch (error) {
    console.error('Error updating category:', error);
    throw new Error('Failed to update category. Please try again later.');
  }
};

/** Toggle enabled state; creates a Firestore override for built-in defaults when needed. */
export const setCategoryEnabled = async (
  restId: string,
  option: CategoryOption,
  enabled: boolean,
  existing?: KhaataCategory
): Promise<KhaataCategory> => {
  if (existing) {
    await updateKhaataCategory(existing.id, { enabled });
    return { ...existing, enabled };
  }

  const id = await addKhaataCategory({
    restId,
    name: option.label,
    value: option.value,
    type: option.type,
    enabled,
  });

  return {
    id,
    restId,
    name: option.label,
    value: option.value,
    type: option.type,
    enabled,
  };
};

export const deleteKhaataCategory = async (id: string): Promise<void> => {
  const db = getFirestore();
  try {
    await deleteDoc(doc(db, COLLECTION, id));
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error('Failed to delete category. Please try again later.');
  }
};
